import AsyncStorage from '@react-native-async-storage/async-storage';
import type { NavigationContainerRef } from '@react-navigation/native';
import type { LocationScopedActivationTaskId } from '../redux/ownerActivationSlice';
import { isGettingStartedNotificationData } from '../utils/gettingStartedNotification';

const PENDING_TASK_NAV_KEY = 'gettingStartedPendingTaskNav';

type RootNav = NavigationContainerRef<any>;

let navigationRef: RootNav | null = null;
let prepareBackOffice: (() => Promise<unknown>) | null = null;
let flushPromise: Promise<boolean> | null = null;

export const setGettingStartedRootNavigationRef = (ref: RootNav | null) => {
  navigationRef = ref;
};

export const setGettingStartedBackOfficePreparation = (
  prepare: (() => Promise<unknown>) | null,
) => {
  prepareBackOffice = prepare;
};

export const queueGettingStartedTaskNavigation = async (
  data: unknown,
): Promise<boolean> => {
  if (!isGettingStartedNotificationData(data)) return false;
  await AsyncStorage.setItem(PENDING_TASK_NAV_KEY, data.taskId);
  return true;
};

export const consumeGettingStartedTaskNavigation =
  async (): Promise<LocationScopedActivationTaskId | null> => {
    const pending = await AsyncStorage.getItem(PENDING_TASK_NAV_KEY);
    if (!pending) return null;
    await AsyncStorage.removeItem(PENDING_TASK_NAV_KEY);
    const data = { type: 'getting_started', taskId: pending };
    return isGettingStartedNotificationData(data) ? data.taskId : null;
  };

/** Routes each nudge to the full feature screen needed to complete its task. */
export const navigateGettingStartedTask = async (
  taskId: LocationScopedActivationTaskId,
): Promise<boolean> => {
  try {
    if (!navigationRef?.isReady()) return false;

    if (taskId === 'explore_backoffice' || taskId === 'add_staff') {
      // On cold start the navigator can become ready one tick before AppNav
      // registers owner auto-login. Keep the task queued until both are ready.
      if (!prepareBackOffice) return false;
      await prepareBackOffice();
    }

    switch (taskId) {
      case 'add_product':
        navigationRef.navigate('App', {
          screen: 'NewProduct',
        });
        break;
      case 'first_sale':
        navigationRef.navigate('App', {
          screen: 'MainScreen',
          params: {
            screen: 'ProductsScreen',
            params: { from: 'HomeScreen' },
          },
        });
        break;
      case 'explore_backoffice':
        navigationRef.navigate('App', {
          screen: 'BackOffice',
          params: { screen: 'Dashboard' },
        });
        break;
      case 'add_staff':
        navigationRef.navigate('App', {
          screen: 'BackOfficeAddEmployee',
        });
        break;
    }
    return true;
  } catch (error) {
    console.log('Getting Started task navigation failed:', error);
    return false;
  }
};

/**
 * If a Getting Started push was tapped while nav wasn't ready, flush now.
 */
export const flushGettingStartedTaskNavigation = async (): Promise<boolean> => {
  if (flushPromise) return flushPromise;

  flushPromise = (async () => {
    const taskId = await consumeGettingStartedTaskNavigation();
    if (!taskId) return false;
    const navigated = await navigateGettingStartedTask(taskId);
    if (!navigated) {
      await AsyncStorage.setItem(PENDING_TASK_NAV_KEY, taskId);
    }
    return navigated;
  })();

  try {
    return await flushPromise;
  } finally {
    flushPromise = null;
  }
};

export const handleGettingStartedNotificationPress = async (
  data: unknown,
): Promise<boolean> => {
  if (!isGettingStartedNotificationData(data)) return false;
  const navigated = await navigateGettingStartedTask(data.taskId);
  if (!navigated) {
    await queueGettingStartedTaskNavigation(data);
  }
  return true;
};
